// src/components/MilestoneTimeline.jsx
//
// Phase C.2 — horizontal deal timeline. Renders 12 milestones in order,
// each dot color-coded (done / current / upcoming / overdue). Click a dot
// to expand an inline detail panel for editing target/actual dates, notes,
// and triggering milestone-specific actions (Financial DD kickoff, etc.).
//
// Pure presentation + editing. Kickoff actions bubble up via `onKickoff`
// so the parent (DealView) can reuse its existing scoped-token + email flow.

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Check, Loader2, ChevronDown, ChevronRight, Send, Calendar, AlertTriangle,
  Mail, Scale, FileSpreadsheet, Briefcase, Handshake, FileSignature, Flag,
  PhoneCall, ClipboardCheck, Building, Sparkles,
} from 'lucide-react';

// Milestone definitions — single source of truth for labels, hints, and
// which milestones get special kickoff actions. Order matches the DB seed.
const MILESTONES = [
  { key: 'first_call',            label: 'First Call',             icon: PhoneCall,      hint: 'Initial discovery call with the seller.' },
  { key: 'nda_sent',              label: 'NDA Sent',               icon: Mail,           hint: 'NDA delivered to seller. Expect ~3 business days to return.' },
  { key: 'nda_returned',          label: 'NDA Returned',           icon: FileSignature,  hint: 'NDA signed and back. Unlocks preliminary info request.' },
  { key: 'prelim_info_received',  label: 'Prelim Info',            icon: ClipboardCheck, hint: "Seller's self-reported calls, revenue, assets — not yet verified." },
  { key: 'internal_review_done',  label: 'Internal Review',        icon: Scale,          hint: 'LFG internal go/no-go decision. Up to 1 week after prelim info.' },
  { key: 'framework_sent',        label: 'Framework Sent',         icon: FileSpreadsheet,hint: 'Valuation + structure draft sent to seller. Up to 1 week after review.' },
  { key: 'loi_sent',              label: 'LOI Sent',               icon: Handshake,      hint: 'Formal letter of intent shipped. Preceded by up to 3 weeks of negotiations.' },
  { key: 'loi_returned',          label: 'LOI Returned',           icon: FileSignature,  hint: 'LOI signed and back (~5 bday turn). Kicks off Financial DD.',
    kickoff: { milestoneKey: 'financial_dd_kickoff', role: 'accountant', label: 'Kickoff Financial DD' } },
  { key: 'financial_dd_done',     label: 'Financial DD',           icon: FileSpreadsheet,hint: 'Cohen & Co. + seller accountant finished. 2–4 weeks.',
    kickoff: { milestoneKey: 'legal_dd_kickoff', role: 'counsel', label: 'Kickoff Legal DD' } },
  { key: 'legal_dd_done',         label: 'Legal DD',               icon: Scale,          hint: 'Dickinson Wright + seller counsel finished. 2–3 weeks.',
    kickoff: { milestoneKey: 'pre_integration_kickoff', role: 'seller', label: 'Start Pre-Integration' } },
  { key: 'pre_integration_done',  label: 'Pre-Integration',        icon: Building,       hint: 'Internal integration prep: titling, ADP, accounts, utilities, banks. 2–4 weeks.' },
  { key: 'close',                 label: 'Close',                  icon: Flag,           hint: 'Deal closed. Post-close integration begins.' },
];

const MILESTONE_META = MILESTONES.reduce((acc, m) => { acc[m.key] = m; return acc; }, {});

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatShortDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function addDaysISO(iso, days) {
  if (!iso) return null;
  const dt = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  dt.setDate(dt.getDate() + (days || 0));
  return dt.toISOString().slice(0, 10);
}

// Track-aware date chain. Each track chains its own milestones from a
// well-defined anchor:
//   pre_loi          → anchored at dealCreatedAt, sequential within track
//   financial_dd     ┐
//   legal_dd         ├ all anchored at the END of pre_loi (loi_returned),
//   pre_integration  ┘ each chained sequentially within its own track
//   close_post       → anchored at the LATEST end across the 3 dd tracks
//                      (the slowest diligence track determines when close
//                      can begin), sequential within track
//
// "End of a track" is the last milestone's actual_date if set, otherwise
// its effective target. So when financial DD finishes early but legal
// DD slips, the close timeline reflects the legal slip — not the
// optimistic financial date.
function withDynamicTargets(milestones, dealCreatedAt) {
  const sorted = [...milestones].sort((a, b) => a.sort_order - b.sort_order);
  const dealStart = dealCreatedAt
    ? (typeof dealCreatedAt === 'string' ? dealCreatedAt.slice(0, 10) : new Date(dealCreatedAt).toISOString().slice(0, 10))
    : null;

  const byTrack = { pre_loi: [], financial_dd: [], legal_dd: [], pre_integration: [], close_post: [] };
  for (const m of sorted) {
    const t = m.track || 'pre_loi';
    (byTrack[t] || (byTrack[t] = [])).push(m);
  }

  const result = new Map();
  const chain = (rows, startAnchor) => {
    let anchor = startAnchor;
    for (const m of rows) {
      const computed = anchor ? addDaysISO(anchor, m.default_offset_days || 0) : null;
      const effective = m.target_date || computed;
      const next = m.actual_date || effective;
      result.set(m.id, { ...m, computed_target_date: computed, effective_target_date: effective });
      anchor = next;
    }
    return anchor;
  };

  // 1. pre_loi from deal creation
  const preLoiEnd = chain(byTrack.pre_loi, dealStart);

  // 2-4. three parallel diligence tracks, each from preLoiEnd
  const ddEnds = [];
  for (const track of ['financial_dd', 'legal_dd', 'pre_integration']) {
    const end = chain(byTrack[track], preLoiEnd);
    if (end) ddEnds.push(end);
  }

  // 5. close_post from the slowest diligence track end (or preLoiEnd if no
  //    diligence milestones exist yet)
  const closeAnchor = ddEnds.length ? ddEnds.reduce((a, b) => (a > b ? a : b)) : preLoiEnd;
  chain(byTrack.close_post, closeAnchor);

  // Return in original sort order so component-level filtering / find() works
  return sorted.map(m => result.get(m.id) || m);
}

// Lane definitions — order, label, accent color, and the visual hint that
// the three middle lanes run in parallel ("diligence cluster").
const LANES = [
  { track: 'pre_loi',          label: 'Pre-LOI',          short: 'Pre-LOI',     accent: '#1A3665', cluster: 'main' },
  { track: 'financial_dd',     label: 'Financial DD',     short: 'Financial',   accent: '#10B981', cluster: 'diligence' },
  { track: 'legal_dd',         label: 'Legal DD',         short: 'Legal',       accent: '#3B82F6', cluster: 'diligence' },
  { track: 'pre_integration',  label: 'Pre-Integration',  short: 'Pre-Integ.',  accent: '#8B5CF6', cluster: 'diligence' },
  { track: 'close_post',       label: 'Close & Post',     short: 'Close',       accent: '#EA580C', cluster: 'main' },
];

function isOverdue(m) {
  const target = m.effective_target_date || m.target_date;
  if (m.actual_date || !target) return false;
  const today = new Date().toISOString().slice(0, 10);
  return target < today;
}

// The "current" milestone is the earliest incomplete one.
function findCurrentKey(milestones) {
  const next = milestones.find(m => !m.actual_date);
  return next?.milestone_key || null;
}

export default function MilestoneTimeline({
  milestones, loading, updateMilestone, primaryColor, isViewer, onKickoff, showToast,
  dealCreatedAt,
}) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Apply dynamic date chain — every milestone gets effective_target_date
  // computed from previous anchor + offset, with explicit target_date as override.
  const sorted = useMemo(
    () => withDynamicTargets(milestones, dealCreatedAt),
    [milestones, dealCreatedAt]
  );
  const currentKey = useMemo(() => findCurrentKey(sorted), [sorted]);

  // When the user marks the currently-expanded milestone as done, advance the
  // expanded panel to the new current milestone so they stay in flow. Skip
  // if no new current (i.e., the deal is fully done) or if the expanded
  // milestone is still incomplete (manual target edit shouldn't move the panel).
  useEffect(() => {
    if (!expandedKey) return;
    const expandedM = sorted.find(m => m.milestone_key === expandedKey);
    if (expandedM?.actual_date && currentKey && currentKey !== expandedKey) {
      setExpandedKey(currentKey);
    }
  }, [sorted, expandedKey, currentKey]);

  const completeCount = sorted.filter(m => m.actual_date).length;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm mb-3 px-4 py-3 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        <span className="text-sm text-gray-400">Loading timeline…</span>
      </div>
    );
  }

  if (!sorted.length) return null;

  const currentMilestone = sorted.find(m => m.milestone_key === currentKey);

  const pctComplete = Math.round((completeCount / sorted.length) * 100);

  return (
    <section
      className="rounded-2xl mb-6 overflow-hidden relative"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
      }}
    >
      {/* Top accent strip — gives the section identity and color presence */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{
          background: `linear-gradient(90deg, ${primaryColor} 0%, #2E4B7C 30%, #5470A8 70%, ${primaryColor}40 100%)`,
        }}
      />
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-6 py-5 hover:bg-[#FAFBFD] transition-colors text-left"
      >
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
            Deal Workflow
          </h2>
          <span className="text-xs tabular-nums" style={{ color: '#94A3B8' }}>
            {completeCount} of {sorted.length} complete · {pctComplete}%
          </span>
          {currentMilestone && !collapsed && (
            <span
              className="text-[11px] px-2 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: `${primaryColor}10`,
                color: primaryColor,
                border: `1px solid ${primaryColor}25`,
              }}
            >
              Next: {MILESTONE_META[currentMilestone.milestone_key]?.label}
              {currentMilestone.effective_target_date ? ` · ${formatShortDate(currentMilestone.effective_target_date)}` : ''}
            </span>
          )}
        </div>
        {collapsed ? <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />}
      </button>

      {!collapsed && (
        <div style={{ borderTop: '1px solid #F0F2F6' }}>
          {/* Branching timeline — main line runs straight across, three
              diligence branches drop below between LOI Returned and Close
              and re-merge into the close trunk. Compact: only the
              diligence "fan" gap takes extra height. */}
          <TimelineWidthBox primaryColor={primaryColor}>
            {(width) => (
              <TimelineLanes
                milestones={sorted}
                currentKey={currentKey}
                expandedKey={expandedKey}
                onSelect={(key) => setExpandedKey(expandedKey === key ? null : key)}
                primaryColor={primaryColor}
                availableWidth={width}
              />
            )}
          </TimelineWidthBox>

          {/* Detail panel */}
          {expandedKey && (
            <MilestoneDetail
              milestone={sorted.find(m => m.milestone_key === expandedKey)}
              isCurrent={expandedKey === currentKey}
              isViewer={isViewer}
              updateMilestone={updateMilestone}
              onKickoff={onKickoff}
              primaryColor={primaryColor}
              showToast={showToast}
              onClose={() => setExpandedKey(null)}
              allSorted={sorted}
            />
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// TimelineWidthBox — measures the available container width and passes
// it to its child via a render-prop. The chart uses this to compute a
// CELL_W that fills the row (instead of leaving whitespace at the right
// edge on wide screens).
// ---------------------------------------------------------------------------
function TimelineWidthBox({ children }) {
  // Measure the INNER content div (no padding) so the chart's totalW
  // doesn't accidentally exceed the viewable area and trigger a
  // horizontal scrollbar on screens that should fit comfortably.
  const innerRef = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!innerRef.current) return;
    const measure = () => setWidth(innerRef.current?.clientWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="px-6 py-6">
      <div ref={innerRef} className="overflow-x-auto">
        {width > 0 ? children(width) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimelineLanes — flowchart-style layout. Each milestone is a rounded card
// with the name + date inside. The pre-LOI sequence runs left-to-right.
// At LOI Returned, a single line branches into three stacked diligence
// cards (Financial / Legal / Pre-Integration), each connected via a
// rectilinear bracket (horizontal-vertical-horizontal). All three branches
// re-merge into the Close card via the mirror bracket pattern, then any
// post-close milestones continue right.
// ---------------------------------------------------------------------------

// Circular milestone dot — the original pattern. The label and date live
// below the dot.
function MilestoneDot({ milestone, isCurrent, isExpanded, onSelect, primaryColor, size = 38 }) {
  const meta = MILESTONE_META[milestone.milestone_key] || { label: milestone.milestone_key };
  const done = !!milestone.actual_date;
  const overdue = isOverdue(milestone);
  const Icon = meta.icon;

  let dotBg = 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)';
  let dotBorder = '#E5E9F0';
  let dotIconColor = '#94A3B8';
  let dotShadow = '0 1px 2px rgba(15, 23, 42, 0.04)';
  if (done) {
    dotBg = 'linear-gradient(180deg, #10B981 0%, #059669 100%)';
    dotBorder = '#059669';
    dotIconColor = '#FFFFFF';
    dotShadow = '0 2px 6px rgba(16, 185, 129, 0.35), 0 1px 2px rgba(16, 185, 129, 0.2)';
  } else if (overdue) {
    dotBg = 'linear-gradient(180deg, #FFFFFF 0%, #FEF2F2 100%)';
    dotBorder = '#EF4444';
    dotIconColor = '#EF4444';
    dotShadow = '0 0 0 4px rgba(239, 68, 68, 0.1), 0 1px 3px rgba(239, 68, 68, 0.2)';
  } else if (isCurrent) {
    dotBg = `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`;
    dotBorder = primaryColor;
    dotIconColor = '#FFFFFF';
    dotShadow = `0 0 0 5px ${primaryColor}18, 0 4px 12px ${primaryColor}50, 0 2px 4px ${primaryColor}30`;
  }

  const iconSize = Math.round(size * 0.42);

  return (
    <button
      type="button"
      onClick={() => onSelect(milestone.milestone_key)}
      className={`relative z-10 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-105 ${isExpanded ? 'scale-110' : ''}`}
      style={{
        width: size, height: size,
        background: dotBg,
        border: `1.5px solid ${dotBorder}`,
        boxShadow: isExpanded ? `0 0 0 4px ${primaryColor}25, ${dotShadow}` : dotShadow,
      }}
      title={meta.label}
    >
      {done ? (
        <Check style={{ width: iconSize, height: iconSize, color: dotIconColor, strokeWidth: 2.5 }} />
      ) : overdue ? (
        <AlertTriangle style={{ width: iconSize, height: iconSize, color: dotIconColor, strokeWidth: 2.2 }} />
      ) : Icon ? (
        <Icon style={{ width: iconSize, height: iconSize, color: dotIconColor, strokeWidth: 2 }} />
      ) : null}
    </button>
  );
}

function MilestoneLabel({ milestone, isCurrent, compact = false }) {
  const meta = MILESTONE_META[milestone.milestone_key] || { label: milestone.milestone_key };
  const done = !!milestone.actual_date;
  const overdue = isOverdue(milestone);
  return (
    <div className={`text-center px-1 ${compact ? 'mt-1.5' : 'mt-2'}`}>
      <div
        className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-medium leading-tight`}
        style={{ color: done ? '#059669' : overdue ? '#DC2626' : isCurrent ? '#0F172A' : '#64748B' }}
      >
        {meta.label}
      </div>
      <div
        className="text-[10px] mt-0.5 tabular-nums leading-tight"
        style={{ color: done ? '#10B981' : overdue ? '#EF4444' : '#94A3B8' }}
      >
        {milestone.actual_date
          ? formatShortDate(milestone.actual_date)
          : milestone.effective_target_date
            ? `${milestone.target_date ? '📌 ' : ''}${formatShortDate(milestone.effective_target_date)}`
            : '—'}
      </div>
    </div>
  );
}

function TimelineLanes({ milestones, currentKey, expandedKey, onSelect, primaryColor, availableWidth }) {
  // Group by track
  const byTrack = useMemo(() => {
    const map = { pre_loi: [], financial_dd: [], legal_dd: [], pre_integration: [], close_post: [] };
    for (const m of milestones) {
      const t = m.track || 'pre_loi';
      if (map[t]) map[t].push(m);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.sort_order - b.sort_order);
    return map;
  }, [milestones]);

  const preLoi = byTrack.pre_loi;
  const closePost = byTrack.close_post;
  const activeBranches = ['financial_dd', 'legal_dd', 'pre_integration']
    .filter(t => byTrack[t].length > 0);

  // Sizing.
  const DOT = 36;
  const BRANCH_DOT = 30;
  // CELL_W is computed dynamically so the dots fill the available width
  // evenly. Total slots on the main line = preLoi.length + (1 for the
  // branch column if any) + closePost.length. Slot widths: each main-
  // line slot is CELL_W, the branch slot is CELL_W with a smaller dot
  // drawn in it (so spacing remains uniform). Min width clamps so dots
  // don't overlap their labels on a very narrow viewport — falls back
  // to scrolling if the container is smaller than the natural minimum.
  const slotCount = preLoi.length + (activeBranches.length > 0 ? 1 : 0) + closePost.length;
  const MIN_CELL_W = 92;
  const CELL_W = slotCount > 0
    ? Math.max(MIN_CELL_W, Math.floor((availableWidth || 0) / slotCount))
    : MIN_CELL_W;
  const BRANCH_CELL_W = CELL_W;
  // Edge-to-edge horizontal gap between adjacent dots, derived from
  // CELL_W. Always equal to (CELL_W - DOT) so spacing reads consistently
  // even when the container resizes.
  const EDGE_GAP = CELL_W - DOT;
  // Row pitch must be tall enough that a branch's date label has room
  // below the dot before the next branch's dot starts. dot 30 + label
  // block ~36 (two lines of small text + margin) + breathing 24 = 90.
  const BRANCH_ROW_H = 90;
  const PAD_TOP = 10;
  const PAD_BOTTOM = 10;
  const HALF = DOT / 2;
  const HALF_B = BRANCH_DOT / 2;

  // X coordinate of each slot's CENTER
  const preLoiCx = preLoi.map((_, i) => CELL_W / 2 + i * CELL_W);
  const lastPreLoiCx = preLoiCx[preLoiCx.length - 1] || 0;
  const lastPreLoiRight = lastPreLoiCx + HALF;

  // Place the branch column so its LEFT edge sits EDGE_GAP right of the
  // LOI Returned dot's right edge, and the first Close dot's LEFT edge
  // sits EDGE_GAP right of the branch column's right edge. This makes
  // the edge-to-edge spacing uniform across the whole timeline.
  const branchColLeft = lastPreLoiRight + EDGE_GAP;
  const branchColCx = branchColLeft + BRANCH_DOT / 2;
  const branchColRight = branchColCx + BRANCH_DOT / 2;

  const firstCloseCx = activeBranches.length > 0
    ? branchColRight + EDGE_GAP + HALF
    : lastPreLoiRight + EDGE_GAP + HALF;
  const closePostCx = closePost.map((_, i) => firstCloseCx + i * CELL_W);

  // Y coordinates
  // Branches stack vertically. Main line is centered around the middle
  // branch so the bracket paths look symmetric.
  const branchCount = Math.max(activeBranches.length, 1);
  // Branch dot CENTERS — pitched by BRANCH_ROW_H which already includes
  // the dot height + label block + breathing room.
  const branchYs = activeBranches.map((_, idx) => PAD_TOP + HALF_B + idx * BRANCH_ROW_H);
  const mainY = activeBranches.length > 0
    ? branchYs[Math.floor((branchYs.length - 1) / 2)] // align with middle branch (visually with Legal DD)
    : PAD_TOP + HALF;
  // Total height = top of first branch dot + (count-1) row pitches + dot
  // + label block + bottom padding. Last branch's label sits below
  // (branchYs[last] + HALF_B), so we need ~36px more for that label.
  const lastBranchY = branchYs.length ? branchYs[branchYs.length - 1] : mainY;
  const totalH = lastBranchY + HALF_B + 36 + PAD_BOTTOM;

  const totalW = (closePost.length > 0
    ? closePostCx[closePost.length - 1] + CELL_W / 2
    : (activeBranches.length > 0 ? branchColRight + BRANCH_GAP_X : lastPreLoiRight)) + 4;

  const DONE_C = '#10B981';
  const IDLE_C = '#CBD5E1';

  // L-bracket path with rounded corners.
  const bracket = (x1, y1, x2, y2, railX, r = 8) => {
    if (y1 === y2) return `M ${x1} ${y1} L ${x2} ${y2}`;
    const sy = Math.sign(y2 - y1);
    return `M ${x1} ${y1} L ${railX - r} ${y1} Q ${railX} ${y1} ${railX} ${y1 + sy * r} L ${railX} ${y2 - sy * r} Q ${railX} ${y2} ${railX + r} ${y2} L ${x2} ${y2}`;
  };

  const lastPreLoi = preLoi[preLoi.length - 1];
  const firstClose = closePost[0];
  const preLoiDone = !!lastPreLoi?.actual_date;

  // Vertical rails sit centered in the gap between LOI Returned and the
  // branch column, and between the branch column and Close.
  const railLeft = lastPreLoiRight + EDGE_GAP / 2;
  const railRight = branchColRight + EDGE_GAP / 2;

  return (
    <div className="relative" style={{ width: totalW, height: totalH, minWidth: '100%' }}>
      {/* SVG connectors */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width={totalW}
        height={totalH}
        style={{ overflow: 'visible' }}
      >
        {/* Pre-LOI: connectors between adjacent dots */}
        {preLoi.map((m, i) => {
          if (i === preLoi.length - 1) return null;
          const x1 = preLoiCx[i] + HALF;
          const x2 = preLoiCx[i + 1] - HALF;
          const done = !!m.actual_date;
          return (
            <line key={`pre-${i}`} x1={x1} y1={mainY} x2={x2} y2={mainY}
              stroke={done ? DONE_C : IDLE_C} strokeWidth="2" />
          );
        })}

        {/* Close-post: connectors between adjacent dots */}
        {closePost.map((m, i) => {
          if (i === closePost.length - 1) return null;
          const x1 = closePostCx[i] + HALF;
          const x2 = closePostCx[i + 1] - HALF;
          const done = !!m.actual_date;
          return (
            <line key={`post-${i}`} x1={x1} y1={mainY} x2={x2} y2={mainY}
              stroke={done ? DONE_C : IDLE_C} strokeWidth="2" />
          );
        })}

        {/* Branch fan-out + fan-in via bracket paths */}
        {activeBranches.length > 0 && lastPreLoi && (
          <g>
            {activeBranches.map((t, idx) => (
              <path
                key={`brOut-${t}`}
                d={bracket(lastPreLoiRight, mainY, branchColLeft, branchYs[idx], railLeft)}
                stroke={preLoiDone ? DONE_C : IDLE_C}
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
            ))}
            {activeBranches.map((t, idx) => {
              const last = byTrack[t][byTrack[t].length - 1];
              const branchDone = !!last?.actual_date;
              const closeLeftX = firstClose ? closePostCx[0] - HALF : branchColRight + BRANCH_GAP_X;
              return (
                <path
                  key={`brIn-${t}`}
                  d={bracket(branchColRight, branchYs[idx], closeLeftX, mainY, railRight)}
                  stroke={branchDone ? DONE_C : IDLE_C}
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                />
              );
            })}
          </g>
        )}

        {/* No branches → straight line LOI → Close */}
        {activeBranches.length === 0 && lastPreLoi && firstClose && (
          <line
            x1={lastPreLoiRight} y1={mainY}
            x2={closePostCx[0] - HALF} y2={mainY}
            stroke={preLoiDone ? DONE_C : IDLE_C} strokeWidth="2"
          />
        )}
      </svg>

      {/* Pre-LOI dots */}
      {preLoi.map((m, i) => (
        <div
          key={m.id}
          className="absolute flex flex-col items-center"
          style={{ left: preLoiCx[i] - CELL_W / 2, top: mainY - HALF, width: CELL_W }}
        >
          <MilestoneDot
            milestone={m}
            isCurrent={m.milestone_key === currentKey}
            isExpanded={expandedKey === m.milestone_key}
            onSelect={onSelect}
            primaryColor={primaryColor}
            size={DOT}
          />
          <MilestoneLabel milestone={m} isCurrent={m.milestone_key === currentKey} />
        </div>
      ))}

      {/* Diligence branch dots */}
      {activeBranches.map((t, idx) => {
        const items = byTrack[t];
        return items.map((m, i) => (
          <div
            key={m.id}
            className="absolute flex flex-col items-center"
            style={{
              left: branchColCx - BRANCH_CELL_W / 2,
              top: branchYs[idx] - HALF_B,
              width: BRANCH_CELL_W,
            }}
          >
            <MilestoneDot
              milestone={m}
              isCurrent={m.milestone_key === currentKey}
              isExpanded={expandedKey === m.milestone_key}
              onSelect={onSelect}
              primaryColor={primaryColor}
              size={BRANCH_DOT}
            />
            <MilestoneLabel milestone={m} isCurrent={m.milestone_key === currentKey} compact />
          </div>
        ));
      })}

      {/* Close-post dots */}
      {closePost.map((m, i) => (
        <div
          key={m.id}
          className="absolute flex flex-col items-center"
          style={{ left: closePostCx[i] - CELL_W / 2, top: mainY - HALF, width: CELL_W }}
        >
          <MilestoneDot
            milestone={m}
            isCurrent={m.milestone_key === currentKey}
            isExpanded={expandedKey === m.milestone_key}
            onSelect={onSelect}
            primaryColor={primaryColor}
            size={DOT}
          />
          <MilestoneLabel milestone={m} isCurrent={m.milestone_key === currentKey} />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MilestoneDetail — inline detail panel shown below the rail when expanded
// ---------------------------------------------------------------------------

function MilestoneDetail({ milestone, isCurrent, isViewer, updateMilestone, onKickoff, primaryColor, showToast, onClose, allSorted = [] }) {
  const meta = MILESTONE_META[milestone.milestone_key] || {};
  // The visible value in the target input is whichever is "live": the user's
  // override (target_date) if set, else the auto-computed value. On save we
  // diff against the override only.
  const isOverridden = !!milestone.target_date;
  const visibleTarget = milestone.target_date || milestone.effective_target_date || '';
  const [target, setTarget] = useState(visibleTarget);
  const [actual, setActual] = useState(milestone.actual_date || '');
  const [notes, setNotes] = useState(milestone.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTarget(milestone.target_date || milestone.effective_target_date || '');
    setActual(milestone.actual_date || '');
    setNotes(milestone.notes || '');
  }, [milestone.id, milestone.target_date, milestone.effective_target_date, milestone.actual_date, milestone.notes]);

  const saveField = async (patch) => {
    if (isViewer) return;
    setSaving(true);
    try {
      await updateMilestone(milestone.id, patch);
    } catch (err) {
      showToast?.('Save failed: ' + err.message, 'error');
    } finally { setSaving(false); }
  };

  // Cascade: any earlier-in-order milestone that's still incomplete
  // implicitly happened too — mark them done with the same date so the
  // timeline doesn't show a "skip" pattern that misrepresents where the
  // deal actually is. Sequential to keep updates predictable; a single
  // failure doesn't block the others.
  const cascadeCompletePredecessors = async (asOfDate) => {
    const idx = allSorted.findIndex(m => m.id === milestone.id);
    if (idx <= 0) return 0;
    const predecessors = allSorted.slice(0, idx).filter(m => !m.actual_date);
    let cascaded = 0;
    for (const p of predecessors) {
      try {
        await updateMilestone(p.id, { actual_date: asOfDate });
        cascaded++;
      } catch (e) {
        console.warn('cascade complete failed for', p.milestone_key, e);
      }
    }
    return cascaded;
  };

  const markComplete = async (asOfDate) => {
    await saveField({ actual_date: asOfDate });
    setActual(asOfDate);
    const cascaded = await cascadeCompletePredecessors(asOfDate);
    if (cascaded > 0) {
      showToast?.(`Also marked ${cascaded} earlier milestone${cascaded === 1 ? '' : 's'} complete`);
    }
  };

  const toggleDone = async () => {
    if (isViewer) return;
    if (milestone.actual_date) {
      // Unmarking only clears THIS milestone. Reverting a chain of
      // completions is a rare, deliberate operation — not automatic.
      await saveField({ actual_date: null });
      setActual('');
    } else {
      await markComplete(new Date().toISOString().slice(0, 10));
    }
  };

  // Called when the user picks a date in the actual_date input directly
  // (vs clicking the toggle button). Same cascade behavior on first set.
  const onActualBlur = async () => {
    if (actual === (milestone.actual_date || '')) return;
    if (!actual) {
      // cleared — just clear this one
      await saveField({ actual_date: null });
      return;
    }
    if (!milestone.actual_date) {
      // transitioning incomplete → complete, cascade as usual
      await markComplete(actual);
    } else {
      // already complete, just updating the date — no cascade
      await saveField({ actual_date: actual });
    }
  };

  return (
    <div
      className="px-6 py-5"
      style={{ borderTop: '1px solid #F0F2F6', backgroundColor: '#FAFBFD' }}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>{meta.label}</span>
            {isCurrent && !milestone.actual_date && (
              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide">
                Current
              </span>
            )}
            {milestone.actual_date && (
              <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide">
                Done
              </span>
            )}
            {isOverdue(milestone) && (
              <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide">
                Overdue
              </span>
            )}
          </div>
          {meta.hint && <p className="text-xs text-gray-600">{meta.hint}</p>}
        </div>
        {!isViewer && (
          <button
            type="button"
            onClick={toggleDone}
            disabled={saving}
            className="flex-shrink-0 px-3 py-1.5 text-xs rounded flex items-center gap-1.5 text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ backgroundColor: milestone.actual_date ? '#6b7280' : '#10b981' }}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {milestone.actual_date ? 'Mark not done' : 'Mark done'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-gray-500 mb-0.5 flex items-center justify-between gap-1">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Target date
              <span className={`px-1 py-px rounded text-[9px] font-medium uppercase tracking-wide ${isOverridden ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                {isOverridden ? 'Pinned' : 'Auto'}
              </span>
            </span>
            {isOverridden && !isViewer && (
              <button
                type="button"
                onClick={() => { setTarget(milestone.effective_target_date || ''); saveField({ target_date: null }); }}
                disabled={saving}
                className="text-[10px] text-blue-600 hover:underline"
                title="Clear the pinned date and let it flow from the chain"
              >
                Reset to auto
              </button>
            )}
          </label>
          <input
            type="date"
            value={target}
            onChange={e => setTarget(e.target.value)}
            onBlur={() => {
              const v = target || null;
              // Only save if the user actually changed the value vs the current override
              if (v !== (milestone.target_date || null)) saveField({ target_date: v });
            }}
            disabled={isViewer || saving}
            className={`w-full text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 ${isOverridden ? 'border-amber-300 bg-amber-50/40' : ''}`}
          />
          {!isOverridden && milestone.computed_target_date && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              Auto: {formatDate(milestone.computed_target_date)} (chains from upstream)
            </p>
          )}
        </div>
        <div>
          <label className="text-[11px] font-semibold text-gray-500 block mb-0.5 flex items-center gap-1">
            <Check className="w-3 h-3" /> Actual date
          </label>
          <input
            type="date"
            value={actual}
            onChange={e => setActual(e.target.value)}
            onBlur={onActualBlur}
            disabled={isViewer || saving}
            className="w-full text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-gray-500 block mb-0.5">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={() => { if (notes !== (milestone.notes || '')) saveField({ notes: notes || null }); }}
            disabled={isViewer || saving}
            placeholder="Optional context"
            className="w-full text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1"
          />
        </div>
      </div>

      {/* Milestone-specific actions */}
      {meta.kickoff && !isViewer && (
        <div className="mt-4 pt-3 border-t border-blue-200 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600">Next step:</span>
          <button
            type="button"
            onClick={() => onKickoff?.({ role: meta.kickoff.role, milestone })}
            className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: primaryColor }}
          >
            <Send className="w-3 h-3" /> {meta.kickoff.label}
          </button>
          <span className="text-[11px] text-gray-500">
            Opens the DD request composer pre-filled for this role.
          </span>
        </div>
      )}

      <div className="mt-3 text-right">
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export { MILESTONES, MILESTONE_META };
